import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, ChevronDown, MapPin, AlertTriangle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { WIZARD_PLANTILLA as T } from "@/constants/testIds";
import { listarPlantillas, aplicarPlantilla } from "@/data/plantillasMesasStore";

/**
 * Sección "Usar una plantilla de mesas" del Paso 3 (sólo MESAS).
 * Aplica una plantilla activa mediante el endpoint transaccional F1 y delega
 * la rehidratación al wizard vía `onAplicado(respuesta)`.
 */
export default function SelectorPlantillaWizard({ eventoId, salonActual, onAplicado }) {
  const [abierto, setAbierto] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [porAplicar, setPorAplicar] = useState(null);
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setCargando(true);
    listarPlantillas({ activa: true })
      .then(setPlantillas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [abierto]);

  const ordenadas = useMemo(() => {
    const arr = [...plantillas];
    arr.sort((a, b) => {
      const am = a.salon === salonActual ? 0 : 1;
      const bm = b.salon === salonActual ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.salon || "").localeCompare(b.salon || "");
    });
    return arr;
  }, [plantillas, salonActual]);

  const confirmar = async () => {
    const p = porAplicar;
    setAplicando(true);
    setError("");
    try {
      const resp = await aplicarPlantilla(p.id, eventoId);
      setPorAplicar(null);
      toast.success("Plantilla aplicada.");
      onAplicado?.(resp, p);
    } catch (e) {
      setError(e.message);
      setPorAplicar(null);
    } finally {
      setAplicando(false);
    }
  };

  return (
    <section
      data-testid={T.seccion}
      className="rounded-2xl border hairline bg-card p-4 md:p-5 shadow-soft"
    >
      <button
        type="button"
        data-testid={T.abrir}
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between gap-3 min-h-[44px]"
      >
        <span className="flex items-center gap-2 font-medium">
          <LayoutGrid className="w-5 h-5 text-accent" strokeWidth={1.7} />
          Usar una plantilla de mesas
        </span>
        <ChevronDown className={cn("w-5 h-5 transition-transform", abierto && "rotate-180")} />
      </button>

      {abierto && (
        <div className="mt-4 space-y-3">
          {error && (
            <div data-testid={T.error} className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
              {error}
            </div>
          )}
          {cargando ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando plantillas…
            </p>
          ) : ordenadas.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay plantillas activas disponibles.</p>
          ) : (
            <ul data-testid={T.lista} className="grid grid-cols-1 gap-2">
              {ordenadas.map((p) => {
                const mismo = p.salon === salonActual;
                return (
                  <li
                    key={p.id}
                    data-testid={T.item(p.id)}
                    className={cn(
                      "rounded-xl border hairline bg-background/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3",
                      mismo && "ring-1 ring-accent/50",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{p.nombre}</span>
                        {mismo && (
                          <span className="text-[10px] uppercase tracking-wide rounded-full bg-accent/15 text-accent px-2 py-0.5">
                            Salón actual
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.salon}</span>
                        <span>Mesas: <b className="text-foreground">{p.totalMesas}</b></span>
                        <span>Capacidad: <b className="text-foreground">{p.capacidad}</b></span>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={T.aplicar(p.id)}
                      onClick={() => setPorAplicar(p)}
                      className="shrink-0 inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Aplicar
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {porAplicar && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div data-testid={T.confirm} role="alertdialog" aria-modal="true"
               className="w-full max-w-md rounded-2xl border hairline bg-card p-6 shadow-xl space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold">Aplicar «{porAplicar.nombre}»</h2>
            </div>
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>Se reemplazará <b>toda</b> la distribución actual de mesas.</li>
              <li>Todas las mesas quedarán <b>disponibles</b>.</li>
              <li>El salón del evento cambiará a <b>{porAplicar.salon}</b>.</li>
              {porAplicar.salon !== salonActual && (
                <li>Al cambiar de salón, los <b>precios anteriores se eliminarán</b> y deberás configurarlos de nuevo.</li>
              )}
              <li>No se alterarán nombre, fecha, imagen, descripción ni límites.</li>
            </ul>
            <div className="flex justify-end gap-3">
              <button type="button" data-testid={T.confirmCancel} onClick={() => setPorAplicar(null)} disabled={aplicando}
                      className="inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-medium border hairline bg-card hover:bg-muted">
                Cancelar
              </button>
              <button type="button" data-testid={T.confirmOk} onClick={confirmar} disabled={aplicando}
                      className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {aplicando && <Loader2 className="w-4 h-4 animate-spin" />} Aplicar plantilla
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
